import { getAnonymousUserId } from "../features/anonymousUser/anonymousUser";
import { getAccessToken } from "../features/auth/authService";
import type {
  AiGeneration,
  AiCostUsageSummary,
  AiModelsResponse,
  ActivityEvent,
  ActivityEventCreateInput,
  AdminResearchOverview,
  AdminResearchUserDetail,
  DeepDiveSession,
  DesignPromptJob,
  Diagnosis,
  GeneratedPromptResponse,
  Idea,
  PromptDeployTarget,
  PreLaunchJob,
  QuestionAnswer,
  QuestionResult,
  SavedIdeaWithIdea,
  SavedStatus,
  SwipeAction,
  SwipeActionType,
  UserProfile,
  VibeCodingTool
} from "./types";

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
};

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("X-VibeGenie-Anonymous-User-Id", getAnonymousUserId());
  const accessToken = await getAccessToken();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, {
    ...options,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      typeof payload === "object" && payload && "error" in payload
        ? String((payload as { error: unknown }).error)
        : "APIリクエストに失敗しました。時間をおいて再度お試しください。";
    throw new ApiError(message, response.status, payload);
  }

  return payload as T;
}

async function uploadRequest<T>(path: string, formData: FormData): Promise<T> {
  const headers = new Headers();
  headers.set("X-VibeGenie-Anonymous-User-Id", getAnonymousUserId());
  const accessToken = await getAccessToken();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

  const response = await fetch(path, {
    method: "POST",
    headers,
    body: formData
  });

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      typeof payload === "object" && payload && "error" in payload
        ? String((payload as { error: unknown }).error)
        : "アップロードに失敗しました。ZIPファイルを確認してもう一度お試しください。";
    throw new ApiError(message, response.status, payload);
  }

  return payload as T;
}

export const apiClient = {
  getProfile: () => request<UserProfile>("/api/profile"),
  updateProfile: (body: Partial<UserProfile>) => request<UserProfile>("/api/profile", { method: "PATCH", body }),
  resetOnboarding: () => request<UserProfile>("/api/profile/reset-onboarding", { method: "POST" }),
  listIdeas: (params?: URLSearchParams) =>
    request<Idea[]>(`/api/ideas${params && params.toString() ? `?${params.toString()}` : ""}`),
  listRecommendedIdeas: () => request<Idea[]>("/api/ideas/recommended"),
  getIdea: (id: string) => request<Idea>(`/api/ideas/${encodeURIComponent(id)}`),
  getSeedSamples: () => request<Idea[]>("/api/ideas/seed-samples"),
  seedIdeas: (ideas?: Idea[]) => request<{ inserted: number; total: number }>("/api/ideas/seed", { method: "POST", body: ideas ? { ideas } : {} }),
  listSavedIdeas: (status?: SavedStatus) =>
    request<SavedIdeaWithIdea[]>(`/api/saved-ideas${status ? `?status=${status}` : ""}`),
  saveIdea: (ideaId: string, status: SavedStatus = "liked") =>
    request<SavedIdeaWithIdea>("/api/saved-ideas", { method: "POST", body: { ideaId, status } }),
  updateSavedIdea: (id: string, body: { status?: SavedStatus; note?: string }) =>
    request<SavedIdeaWithIdea>(`/api/saved-ideas/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  deleteSavedIdea: (id: string) =>
    request<{ ok: true }>(`/api/saved-ideas/${encodeURIComponent(id)}`, { method: "DELETE" }),
  createSwipe: (ideaId: string, action: SwipeActionType) =>
    request<{ swipe: SwipeAction; savedIdea?: SavedIdeaWithIdea }>("/api/swipes", {
      method: "POST",
      body: { ideaId, action }
    }),
  listSwipes: () => request<SwipeAction[]>("/api/swipes"),
  getQuestionResult: (answers: QuestionAnswer[]) =>
    request<QuestionResult>("/api/questions/result", { method: "POST", body: { answers } }),
  getQuestionHistory: () => request<Array<{ id: string; answers: QuestionAnswer[]; resultIdeaIds: string[]; createdAt: string }>>("/api/questions/history"),
  getAiModels: () => request<AiModelsResponse>("/api/ai/models"),
  getAiCostUsage: () => request<AiCostUsageSummary>("/api/ai/cost-usage"),
  generatePrompt: (body: { ideaId: string; targetTool: VibeCodingTool; deployTarget?: PromptDeployTarget; model?: string; customInstructions?: string }) =>
    request<GeneratedPromptResponse>("/api/ai/generate-prompt", { method: "POST", body }),
  createDeepDiveSession: (body?: { model?: string }) =>
    request<DeepDiveSession>("/api/deep-dive/sessions", { method: "POST", body: body ?? {} }),
  getDeepDiveSession: (id: string) =>
    request<DeepDiveSession>(`/api/deep-dive/sessions/${encodeURIComponent(id)}`),
  answerDeepDiveQuestion: (id: string, body: { answer: string; model?: string }) =>
    request<DeepDiveSession>(`/api/deep-dive/sessions/${encodeURIComponent(id)}/answers`, { method: "POST", body }),
  backDeepDiveQuestion: (id: string) =>
    request<DeepDiveSession>(`/api/deep-dive/sessions/${encodeURIComponent(id)}/back`, { method: "POST", body: {} }),
  finalizeDeepDiveSession: (id: string, body?: { model?: string }) =>
    request<DeepDiveSession>(`/api/deep-dive/sessions/${encodeURIComponent(id)}/finalize`, { method: "POST", body: body ?? {} }),
  generateDeepDivePrompt: (id: string, body: { targetTool: VibeCodingTool; deployTarget?: PromptDeployTarget; model?: string; customInstructions?: string }) =>
    request<GeneratedPromptResponse>(`/api/deep-dive/sessions/${encodeURIComponent(id)}/generate-prompt`, { method: "POST", body }),
  improvePrompt: (body: { prompt: string; purpose?: string; targetTool: VibeCodingTool; model?: string }) =>
    request<GeneratedPromptResponse>("/api/ai/improve-prompt", { method: "POST", body }),
  generateIdeas: (body: {
    category?: string;
    technologies?: string;
    estimatedTime?: string;
    goal?: string;
    targetUser?: string;
    model?: string;
  }) => request<{ model: string; outputText: string }>("/api/ai/generate-ideas", { method: "POST", body }),
  listAiGenerations: () => request<AiGeneration[]>("/api/ai/generations"),
  recordActivityEvent: (body: ActivityEventCreateInput) =>
    request<ActivityEvent>("/api/activity/events", { method: "POST", body }),
  getAdminResearchOverview: () => request<AdminResearchOverview>("/api/admin/research"),
  getAdminResearchUserDetail: (anonymousUserId: string) =>
    request<AdminResearchUserDetail>(`/api/admin/research/users/${encodeURIComponent(anonymousUserId)}`),
  listDiagnoses: () => request<Diagnosis[]>("/api/diagnoses"),
  getDiagnosis: (id: string) => request<Diagnosis>(`/api/diagnoses/${encodeURIComponent(id)}`),
  uploadDiagnosisZip: (formData: FormData) =>
    uploadRequest<{ diagnosisId: string; resultUrl: string }>("/api/diagnoses/upload", formData),
  listPreLaunchJobs: () => request<PreLaunchJob[]>("/api/pre-launch/history"),
  getPreLaunchJob: (id: string) => request<PreLaunchJob>(`/api/pre-launch/${encodeURIComponent(id)}`),
  uploadPreLaunchZip: (formData: FormData) =>
    uploadRequest<{ preLaunchJobId: string; resultUrl: string }>("/api/pre-launch/upload", formData),
  listDesignPromptJobs: () => request<DesignPromptJob[]>("/api/design/history"),
  getDesignPromptJob: (id: string) => request<DesignPromptJob>(`/api/design/${encodeURIComponent(id)}`),
  uploadDesignPromptZip: (formData: FormData) =>
    uploadRequest<{ designPromptJobId: string; resultUrl: string }>("/api/design/prompts", formData)
};
